import { useEffect, useState } from 'react'
import { BrowserRouter, useLocation } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import App from './App'
import { ErrorGlobal } from './layouts/error/errorGlobal'
import { validateEnvVariables } from './utils/validateEnv'
import { validateApiCredentials } from './utils/validateApi'
import { PageLoadingProvider } from './context/PageLoadingContext'

const AppWrapper: React.FC = () => (
    <BrowserRouter>
        <HelmetProvider>
            <ValidatedApp />
        </HelmetProvider>
    </BrowserRouter>
)

const ValidatedApp: React.FC = () => {
    const location = useLocation()
    // Fragment-only navigation keeps the mounted page and native anchor scrolling.
    const navigationKey = location.pathname + location.search
    const [attempt, setAttempt] = useState(0)
    const [validation, setValidation] = useState<{
        navigationKey: string
        attempt: number
        valid: boolean
    } | null>(null)

    useEffect(() => {
        const controller = new AbortController()

        const validateConfig = async () => {
            let valid = false
            try {
                valid =
                    validateEnvVariables() &&
                    (await validateApiCredentials(controller.signal))
            } catch {
                valid = false
            }
            // A newer navigation/retry or unmount owns the UI after cancellation.
            if (!controller.signal.aborted) {
                setValidation({ navigationKey, attempt, valid })
            }
        }

        void validateConfig()
        return () => controller.abort()
    }, [navigationKey, attempt])

    // Results only apply to the navigation and manual attempt that started them.
    if (
        !validation ||
        validation.navigationKey !== navigationKey ||
        validation.attempt !== attempt
    ) {
        return (
            <div
                role="status"
                aria-live="polite"
                className="flex min-h-screen items-center justify-center"
            >
                Chargement du site…
            </div>
        )
    }

    if (!validation.valid) {
        return <ErrorGlobal onRetry={() => setAttempt((value) => value + 1)} />
    }

    return (
        <PageLoadingProvider>
            <App />
        </PageLoadingProvider>
    )
}

export default AppWrapper
